'use client';

import { type ReactNode, useEffect, useState } from 'react';

import type { HomeAppShot, HomeAppShotId } from '../../_content/home';
import { MARKETING_IMG, idx } from '../../_lib/marketing-utils';
import { MarketingImage } from '../ui/marketing-image';

/** Wie lange ein Screen steht, bevor die Diashow weiterschaltet (J9). */
const SLIDE_MS = 7000;

/**
 * Bild, Beschnitt und Grösse je Screen — sprachunabhängig, deshalb hier und
 * nicht im Inhalt (der DE-Screenshot steht bewusst in allen Sprachen, wie
 * zuvor beim Produkt-Blick).
 *
 * 🔴 Die Beschnitt-Klasse ist kein Beiwerk. Alle drei Aufnahmen sind
 * 1440-Viewport-Frames, aber mit sehr verschiedenen Höhen; ohne die eigene
 * Klasse erbt ein Querformat-Screen den Hochformat-Beschnitt der Grundregel
 * (`aspect-ratio: 1440 / 1650` in marketing-extra.css) und verliert links und
 * rechts je gut 500 px.
 */
const SHOT_ASSETS: Record<
  HomeAppShotId,
  { file: string; shotClass: string; width: number; height: number }
> = {
  vermietung: {
    file: 'vermietung-desktop-v4.png',
    shotClass: 'shot--vermietung',
    width: 2880,
    height: 4680,
  },
  startseite: {
    file: 'startseite-desktop-v2.png',
    shotClass: 'shot--startseite',
    width: 2880,
    height: 2460,
  },
  bewerberprofil: {
    /*
     * 🔴 v4 (03.09.2026, zweiter Tausch am selben Tag) — WARUM DAS BILD
     * GETAUSCHT WURDE, UND WARUM JEDES MAL MIT NEUEM DATEINAMEN.
     *
     * Design hat Blatt E1 korrigiert: das Häkchen am Anforderungsprofil ist
     * durch das WORT ersetzt.
     *
     *     gefordert 3,0 ✓   →   gefordert 3,0 · erfüllt
     *     1 offen           →   1 noch offen
     *     wie gewünscht ✓   →   wie gewünscht · erfüllt
     *
     * Das ist keine Kosmetik, sondern die Regel „Grün heißt «erfüllt», nie
     * «gut»": ein Häkchen liest sich als Bewertung der Person, das Wort nennt
     * einen Abgleich gegen das Anforderungsprofil. Das Bild auf der Startseite
     * warb bis dahin mit der Fassung, die genau diese Grenze verwischt.
     *
     * ⚠️ UND EIN ZWEITER GRIFF, den erst die Rückfrage zutage gebracht hat:
     * v3 zeigte „Hauptbewerber", das Bild davor „Hauptbewerberin". Ich hatte
     * das mit dem Blatt mitwandern lassen und nachgefragt statt es
     * stillschweigend zu übernehmen — zu Recht:
     *
     *   „«Hauptbewerberin» war eine bewusste Wahl — die Beispielperson ist
     *    eine Frau, und die männliche Form hätte sie unsichtbar gemacht."
     *
     * E1 ist nachgezogen, und dies ist die Aufnahme danach. „Kaution 3.720 €"
     * statt „in einer Summe" bleibt dagegen: „der Betrag ist die Angabe, die
     * Zahlweise steht darunter."
     *
     * Der Dateiname wandert jedes Mal mit (v2 → v3 → v4): die Datei liegt
     * unter `public/images/**` und wird mit langer Frist ausgeliefert. Ein
     * Tausch am gleichen Namen ließe Bestandsbesucher das alte Bild sehen,
     * ohne dass jemand den Fehler bemerkt.
     */
    file: 'bewerber-profil-desktop-v4.png',
    shotClass: 'shot--bewerberprofil',
    width: 2880,
    /*
     * 🔴 2142, nicht 2140 — aus dem PNG-Kopf gemessen (`sips`), nicht
     * fortgeschrieben. Die zwei Pixel kommen aus der Aufnahme: der gezeichnete
     * Rahmen ist 1440 × 1070, und die Bildpunkt-Rundung bei doppelter
     * Auflösung ergibt 2142 statt 2140.
     *
     * Wer sie ändert, ändert `shot--bewerberprofil` in `marketing-extra.css`
     * MIT — dort steht dieselbe Zahl als `aspect-ratio`, und läuft sie
     * auseinander, beschneidet der Rahmen das Bild.
     */
    height: 2142,
  },
};

/**
 * Home-Abschnitt „Aus der App" (UMSETZUNG.md §4, J3 + J9).
 *
 * Echte App-Screens, gerahmt im Marketing-Karten-Look — kein App-Chrome
 * nachgebaut, kein App-Design-System in den Marketing-Seiten (§7). Umschalter
 * mit `role="tab"`/`aria-selected`; jeder Reiter zeigt genau einen Screen.
 *
 * Diashow: wechselt alle 7 s, hält beim Überfahren der Reiter an und stoppt
 * DAUERHAFT, sobald jemand selbst einen Reiter wählt — danach entscheidet der
 * Nutzer, nicht der Timer. Bei `prefers-reduced-motion: reduce` läuft sie gar
 * nicht erst an.
 *
 * 🔴 Kein `reveal` auf der gewechselten Figur. Der `RevealController` scannt
 * einmal je Route; ein `.reveal`, das erst beim Reiterwechsel in den Baum
 * kommt, bekäme nie `is-visible` und bliebe auf `opacity: 0`. Deshalb trägt
 * der stehende Rahmen das `reveal`, nicht die wechselnde Figur.
 */
export function AppShowcase({
  eyebrow,
  headline,
  lead,
  legend,
  tabsAria,
  shots,
}: {
  eyebrow: string;
  headline: ReactNode;
  lead: string;
  legend: string[];
  tabsAria: string;
  shots: HomeAppShot[];
}) {
  const [active, setActive] = useState(0);
  const [manual, setManual] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (manual || paused) return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const timer = window.setInterval(
      () => setActive((cur) => (cur + 1) % shots.length),
      SLIDE_MS,
    );

    return () => window.clearInterval(timer);
  }, [manual, paused, shots.length]);

  const shot = shots[active];
  const asset = shot ? SHOT_ASSETS[shot.id] : undefined;

  return (
    <section
      className="section section--muted app-section"
      id="app"
      aria-labelledby="app-headline"
    >
      <div className="container">
        <div style={{ maxWidth: '680px' }}>
          <span className="eyebrow reveal">{eyebrow}</span>
          <h2 className="h-display-md reveal" style={idx(1)} id="app-headline">
            {headline}
          </h2>
          <p className="lead reveal" style={idx(2)}>
            {lead}
          </p>
          <ul className="product-glimpse-legend reveal" style={idx(3)}>
            {legend.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <div
          className="appshot-tabs reveal"
          style={idx(3)}
          role="tablist"
          aria-label={tabsAria}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {shots.map((s, i) => (
            <button
              key={s.id}
              id={`appshot-tab-${s.id}`}
              type="button"
              className="appshot-tab"
              role="tab"
              aria-selected={i === active}
              aria-controls="appshot-panel"
              onClick={() => {
                setActive(i);
                setManual(true);
              }}
            >
              {s.tabLabel}
            </button>
          ))}
        </div>

        <div
          className="appshot appshot--wide reveal"
          style={idx(4)}
          id="appshot-panel"
          role="tabpanel"
          aria-labelledby={shot ? `appshot-tab-${shot.id}` : undefined}
        >
          {shot && asset ? (
            <figure className="appshot-figure">
              <MarketingImage
                className={asset.shotClass}
                src={`${MARKETING_IMG}/app/${asset.file}`}
                width={asset.width}
                height={asset.height}
                /* 🔴 KEIN `key` auf der Figur oder dem Bild — das ist gewollt.
                   Ohne `key` behält React denselben <img>-Knoten und patcht
                   nur `src`/`class`; der Browser lädt das neue Bild sofort.
                   Mit `key` käme bei jedem Reiterwechsel ein FRISCHER Knoten
                   in den Baum, und für nachträglich eingefügte Knoten wird
                   der Lazy-Kandidat nicht mehr ausgewertet — der Fetch würde
                   nie starten und die Karte bliebe leer (J9, dort belegt).
                   `lazy` steht deshalb nur am zuerst gezeigten Screen: der
                   Abschnitt liegt weit unter der Falz, und beim Wechsel ist
                   der Knoten längst im Sichtfeld. */
                loading={active === 0 ? 'lazy' : undefined}
                decoding="async"
                alt={shot.alt}
              />
              <figcaption>
                <span className="appshot-badge">{shot.badge}</span>
                <strong>{shot.strong}</strong>
                {shot.rest}
              </figcaption>
            </figure>
          ) : null}
        </div>
      </div>
    </section>
  );
}
